import { ApiError, App, APP_NAME, SupportRequestsApiService } from '@agilox/common';
import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { States } from '@agilox/ui-common';
import { ButtonComponent, NotifyComponent, TextareaComponent } from '@agilox/ui';
import { Component, computed, inject, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { form, FormField, minLength, required } from '@angular/forms/signals';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserStoreService } from '@user-store';
import { catchError, EMPTY, exhaustMap, filter, Subject, tap } from 'rxjs';
import { ToastService } from '../../toast/toast.service';

const MIN_DESCRIPTION_LENGTH = 100;

@Component({
	selector: 'ui-help-content',
	templateUrl: './help-content.component.html',
	imports: [ButtonComponent, FormField, NotifyComponent, TextareaComponent, TranslateModule],
})
export class HelpContentComponent {
	private appName = inject<App>(APP_NAME);
	private document = inject(DOCUMENT);
	private supportRequestsApiService = inject(SupportRequestsApiService);
	private toastService = inject(ToastService);
	private translate = inject(TranslateService);
	private userStoreService = inject(UserStoreService);

	closed = output<void>();

	private descriptionModel = signal<string>('');

	description = form(this.descriptionModel, (path) => {
		required(path);
		minLength(path, MIN_DESCRIPTION_LENGTH);
	});

	descriptionLength = computed(() => this.descriptionModel().length);

	rateLimited = signal<boolean>(false);

	sending = signal<boolean>(false);

	protected readonly MIN_DESCRIPTION_LENGTH = MIN_DESCRIPTION_LENGTH;

	private submit = new Subject<void>();

	constructor() {
		this.submit
			.pipe(
				filter(() => this.description().valid()),
				tap(() => {
					this.rateLimited.set(false);
					this.sending.set(true);
				}),
				exhaustMap(() =>
					this.supportRequestsApiService
						.create({
							app: this.appName,
							description: this.descriptionModel().trim(),
							url: this.document.defaultView?.location.href ?? '',
							userId: this.userStoreService.user()?.id ?? '',
						})
						.pipe(
							catchError((error: HttpErrorResponse) => {
								this.sending.set(false);
								if (error.status === HttpStatusCode.TooManyRequests) {
									this.rateLimited.set(true);
									return EMPTY;
								}
								this.toastService.show(this.getErrorMessage(error), States.Warning);
								return EMPTY;
							})
						)
				),
				takeUntilDestroyed()
			)
			.subscribe(() => {
				this.sending.set(false);
				this.toastService.show(this.translate.instant('help_overlay.success'), States.Success);
				this.closed.emit();
			});
	}

	public onSubmitClick(): void {
		this.submit.next();
	}

	public onCancelClick(): void {
		this.closed.emit();
	}

	private getErrorMessage(error: HttpErrorResponse): string {
		const apiErrors: ApiError[] = error.error?.errors ?? [];
		return apiErrors[0]?.detail ?? this.translate.instant('help_overlay.error');
	}
}
